import { useEffect, useRef } from "react";
import { EditorView, lineNumbers, keymap } from "@codemirror/view";
import { EditorState } from "@codemirror/state";
import { json } from "@codemirror/lang-json";
import { lintGutter, linter, type Diagnostic } from "@codemirror/lint";
import { defaultKeymap, history, historyKeymap } from "@codemirror/commands";
import { search, searchKeymap } from "@codemirror/search";
import {
  foldGutter,
  bracketMatching,
  syntaxHighlighting,
  defaultHighlightStyle,
} from "@codemirror/language";
import { useEditorStore } from "@/store";
import { validateStyleSpec } from "@/lib/validation";

/** Creates a lint source that validates JSON against the MapLibre style spec. */
function createStyleLinter() {
  return linter(
    (view) => {
      const text = view.state.doc.toString();
      const diagnostics: Diagnostic[] = [];

      try {
        const parsed = JSON.parse(text) as unknown;
        const errors = validateStyleSpec(parsed);
        for (const err of errors) {
          diagnostics.push({
            from: 0,
            to: Math.min(text.length, 1),
            severity: err.severity === "warning" ? "warning" : "error",
            message: `${err.path ? `[${err.path}] ` : ""}${err.message}`,
          });
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        const posMatch = msg.match(/position (\d+)/);
        const pos = posMatch ? Number(posMatch[1]) : 0;
        diagnostics.push({
          from: Math.min(pos, Math.max(text.length - 1, 0)),
          to: Math.min(pos + 1, text.length),
          severity: "error",
          message: `JSON syntax error: ${msg}`,
        });
      }

      return diagnostics;
    },
    { delay: 500 },
  );
}

export function JsonEditor() {
  const containerRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const style = useEditorStore((s) => s.style);
  const styleVersion = useEditorStore((s) => s.styleVersion);
  const setStyle = useEditorStore((s) => s.setStyle);
  const selfUpdate = useRef(false);
  const lastVersion = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Initialize CodeMirror
  useEffect(() => {
    if (!containerRef.current) return;

    const initialDoc = JSON.stringify(style, null, 2);

    const updateListener = EditorView.updateListener.of((update) => {
      if (!update.docChanged || selfUpdate.current) return;

      // Debounced sync to store
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        const text = update.state.doc.toString();
        try {
          const parsed = JSON.parse(text);
          selfUpdate.current = true;
          setStyle(parsed);
          lastVersion.current = useEditorStore.getState().styleVersion;
          selfUpdate.current = false;
        } catch {
          // Invalid JSON — linter will show it
        }
      }, 300);
    });

    const state = EditorState.create({
      doc: initialDoc,
      extensions: [
        lineNumbers(),
        foldGutter(),
        bracketMatching(),
        history(),
        search(),
        json(),
        syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
        lintGutter(),
        createStyleLinter(),
        updateListener,
        keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
        EditorView.theme({
          "&": {
            height: "100%",
            fontSize: "12.5px",
            fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', monospace",
          },
          ".cm-scroller": {
            overflow: "auto",
          },
          ".cm-gutters": {
            backgroundColor: "var(--color-surface-sunken)",
            borderRight: "1px solid var(--color-border-subtle)",
            color: "var(--color-text-muted)",
          },
          ".cm-activeLineGutter": {
            backgroundColor: "var(--color-accent-muted)",
          },
          ".cm-activeLine": {
            backgroundColor: "var(--color-accent-muted)",
          },
          ".cm-selectionBackground": {
            backgroundColor: "var(--color-accent-muted) !important",
          },
        }),
      ],
    });

    const view = new EditorView({
      state,
      parent: containerRef.current,
    });

    viewRef.current = view;
    lastVersion.current = styleVersion;

    return () => {
      clearTimeout(debounceTimer.current);
      view.destroy();
    };
    // Only mount once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync store → editor when style changes from section editor
  useEffect(() => {
    const view = viewRef.current;
    if (!view || selfUpdate.current || styleVersion === lastVersion.current) return;
    lastVersion.current = styleVersion;

    const newDoc = JSON.stringify(style, null, 2);
    const currentDoc = view.state.doc.toString();
    if (newDoc === currentDoc) return;

    selfUpdate.current = true;
    view.dispatch({
      changes: { from: 0, to: view.state.doc.length, insert: newDoc },
    });
    selfUpdate.current = false;
  }, [style, styleVersion]);

  return <div ref={containerRef} className="h-full w-full overflow-hidden" />;
}
